import React, { useId } from 'react';

/**
 * Shared text-input primitives — codify the app's dominant field pattern
 * (app-design skill: focus ring is the indigo lineage) instead of per-site
 * inline Tailwind.
 *
 * Dark mode is automatic: src/index.css styles bare input/select/textarea
 * elements under `.dark` (background, border, placeholder). Keep light-mode
 * classes plain here — never add explicit dark: overrides.
 */

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

const FIELD =
  'w-full  border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none disabled:opacity-50';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className = '', ...rest },
  ref
) {
  return <input ref={ref} className={`${FIELD} ${className}`} {...rest} />;
});

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { className = '', rows = 3, ...rest },
  ref
) {
  return <textarea ref={ref} rows={rows} className={`${FIELD} ${className}`} {...rest} />;
});

/** Label + control + hint/error wrapper. Pass the control as children. */
export function FormField(props: {
  label: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const generated = useId();
  const id = props.htmlFor ?? generated;
  return (
    <div className={`flex flex-col gap-1.5 ${props.className ?? ''}`}>
      <label htmlFor={id} className="text-sm font-medium text-[var(--text-sec)]">
        {props.label}
      </label>
      {React.isValidElement(props.children)
        ? React.cloneElement(props.children as React.ReactElement<{ id?: string }>, { id })
        : props.children}
      {props.error ? (
        <p className="text-xs text-[var(--coral)]">{props.error}</p>
      ) : props.hint ? (
        <p className="text-xs text-[var(--text-ter)]">{props.hint}</p>
      ) : null}
    </div>
  );
}
